import {
  Box,
  ContentBlock,
  Heading,
  Stack,
  TextLink,
  Text,
} from 'braid-design-system';
import { useEffect } from 'react';

import {
  EVENT_NAME_ALERT_VIEWED,
  EVENT_NAME_CTA_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import {
  FEATURE_PER_UNIT_BILLING,
  useFeatureToggleData,
} from 'src/app/shared/featureToggle';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { useLocalisedRoutes } from 'src/app/v1/views/hooks/useLocalisedRoutes';
import PageLayout from 'src/app/v1/views/layouts/PageLayout';
import type { TrackingEvent } from 'src/types/trackingData';

import { ErrorSvg } from './ErrorSvg';

interface IMultiLocationUnavailable {
  jobId?: string;
}

interface IHandleSendLinkEventProps {
  eventData?: TrackingEvent;
}

export const MultiLocationUnavailableSecondaryText: React.FC<{
  copyJobUrl?: string;
}> = ({ copyJobUrl }) => {
  const isPerUnitBilling = useFeatureToggleData(FEATURE_PER_UNIT_BILLING);
  if (copyJobUrl) {
    const onClickCopyJobUrl = () => {
      const eventData: TrackingEvent = {
        eventName: EVENT_NAME_CTA_PRESSED,
        actionOrigin: 'multi location not available',
        callToAction: 'copy ad',
      };
      const sendLinkEventProps: IHandleSendLinkEventProps = {
        eventData,
      };
      handleSendLinkEvent(sendLinkEventProps);
    };
    return (
      <Text tone="secondary">
        {!isPerUnitBilling && (
          <>
            Right now, you can only add locations in some regions. <br />
          </>
        )}
        <TextLink href={copyJobUrl} onClick={onClickCopyJobUrl}>
          Copy your ad
        </TextLink>{' '}
        to post it to more locations.
      </Text>
    );
  }
  return (
    <Text tone="secondary">
      We are unable to determine the job you are trying to repost.
    </Text>
  );
};

const handleSendLinkEvent = ({ eventData }: IHandleSendLinkEventProps) => {
  getTrackingAdapter.sendLinkEvent(eventData);
};

export const MultiLocationUnavailable = ({
  jobId,
}: IMultiLocationUnavailable) => {
  const { getCopyJobRoute } = useLocalisedRoutes();
  const copyJobUrl =
    typeof jobId === 'undefined' ? undefined : getCopyJobRoute(jobId);

  useEffect(() => {
    const eventData: TrackingEvent = {
      eventName: EVENT_NAME_ALERT_VIEWED,
      alertShown: [
        {
          tone: 'critical',
          position: 'content',
          message: 'Add location isn’t available',
          messageCode: 'Multi location not available',
        },
      ],
    };
    const sendLinkEventProps: IHandleSendLinkEventProps = {
      eventData,
    };
    handleSendLinkEvent(sendLinkEventProps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <PageLayout hasProgressBar={false}>
      <ContentBlock width="small">
        <Box paddingY="xxlarge">
          <Box background="surface" paddingY="xlarge" paddingX="gutter">
            <Stack space="medium" align="center">
              <Box
                style={{
                  width: '128px',
                  height: '128px',
                }}
              >
                <ErrorSvg />
              </Box>
              <Stack space="gutter" align="center">
                <Heading level="3">Add locations isn&apos;t available</Heading>
                <MultiLocationUnavailableSecondaryText
                  copyJobUrl={copyJobUrl}
                />
              </Stack>
            </Stack>
          </Box>
        </Box>
      </ContentBlock>
    </PageLayout>
  );
};
